<template>
  <v-container fluid>
    <div class="toolbar d-flex flex-row flex-wrap justify-space-between align-center elevation-3 mb-3">
      <span>
        Performance Evaluations - {{ title }}
      </span>
      <h3>
        <span>
          Total Score (%):
        </span>
        <span :class="grandTotalScore === 100 ? 'green--text' : 'red--text'">
          {{ grandTotalScore | showNumberFormat }}
        </span>
        <span>
          / 100
        </span>
      </h3>
    </div>

    <v-row class="px-5">
      <v-col
        v-for="(week, index) in items"
        :key="index"
        cols="12"
        md="6">
        <div class="card-container elevation-3">
          <div class="d-flex flex-row flex-wrap justify-space-between align-center">
            <h3>
              {{ week.weekTitle }}
            </h3>
            <h3 v-if="week.topics.length">
              <span>
                Total Score (%):
              </span>
              <span :class="week.totalScore === 100 ? 'green--text' : 'red--text'">
                {{ week.totalScore | showNumberFormat }}
              </span>
              <span>
                / 100
              </span>
            </h3>
          </div>
          <v-skeleton-loader
            v-if="loading"
            type="table" />
          <v-data-table
            v-else-if="week.topics.length"
            :headers="headers"
            :items="week.topics"
            :items-per-page="-1"
            :loading="loading"
            hide-default-footer>
            <template #[`item.name`]="{ item }">
              <v-tooltip top>
                <template #activator="{ on, attrs }">
                  <span
                    class="text-decoration-underline"
                    style="cursor: pointer;"
                    v-bind="attrs"
                    v-on="on">
                    {{ item.name }}
                  </span>
                </template>
                <div style="white-space: pre-line;">
                  {{ item.formula }}
                </div>
              </v-tooltip>
            </template>
            <template #[`item.target`]="{ item }">
              <span v-if="item.available">
                {{ item.actualTarget | showNumberFormat }} / {{ item.target | showNumberFormat }}
              </span>
              <span v-else>
                - / -
              </span>
              <span v-if="item.conditionType === 'percent'">
                (%)
              </span>
            </template>
            <template #[`item.weight`]="{ item }">
              <span v-if="item.available">
                {{ item.actualWeight | showNumberFormat }} / {{ item.weight | showNumberFormat }}
              </span>
              <span v-else>
                - / -
              </span>
            </template>
          </v-data-table>
          <p
            v-else
            class="text-center py-10">
            ไม่มีข้อมูล
          </p>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import PerformanceEvaluationsProvider from '@/resources/PerformanceEvaluationsProvider'
import WarehouseProvider from '@/resources/WarehouseProvider'
import EvaluationTopics from '@/assets/json/EvaluationTopics.json'

const PerformanceEvaluationsService = new PerformanceEvaluationsProvider()
const WarehouseService = new WarehouseProvider()

export default {
  data () {
    return {
      items: [
        {
          id: null,
          week: 1,
          weekTitle: 'First week (Day 1 - 7)',
          month: null,
          year: null,
          warehouse: null,
          topics: [],
          totalScore: 0
        },
        {
          id: null,
          week: 2,
          weekTitle: 'Second week (Day 8 - 14)',
          month: null,
          year: null,
          warehouse: null,
          topics: [],
          totalScore: 0
        },
        {
          id: null,
          week: 3,
          weekTitle: 'Third week (Day 15 - 21)',
          month: null,
          year: null,
          warehouse: null,
          topics: [],
          totalScore: 0
        },
        {
          id: null,
          week: 4,
          weekTitle: 'Last week (Day 22 - End of month)',
          month: null,
          year: null,
          warehouse: null,
          topics: [],
          totalScore: 0
        }
      ],
      warehouse: null,
      query: {
        month: this.$dayjs().month() + 1,
        year: this.$dayjs().year(),
        warehouseId: null
      },
      months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      weeks: [1, 2, 3, 4],
      mainTopics: EvaluationTopics,
      headers: [
        {
          text: 'Topic',
          value: 'name',
          sortable: false
        },
        {
          text: 'Target',
          value: 'target',
          sortable: false,
          align: 'center',
          width: '200px'
        },
        {
          text: 'Weight (%)',
          value: 'weight',
          sortable: false,
          align: 'center',
          width: '200px'
        }
      ],
      loading: true
    }
  },
  computed: {
    title () {
      return `${this.warehouse ? this.warehouse.name : 'Unknown'} (${this.query.month}/${this.query.year})`
    },
    grandTotalScore () {
      const reports = this.items.filter((item) => item.topics.length)
      const totalScore = reports.reduce((score, item) => score + item.totalScore, 0)

      return reports.length ? totalScore / reports.length : 0
    }
  },
  async mounted () {
    this.query.warehouseId = this.$route.params.id
    this.query.year = Number(this.$route.query.year)
    this.query.month = Number(this.$route.query.month)

    await this.getWarehouse()
    this.getItems()
  },
  methods: {
    async getWarehouse () {
      try {
        this.loading = true

        const { data } = await WarehouseService.getWarehouseById(this.query.warehouseId)

        this.warehouse = data
      } catch (error) {
        console.error('getWarehouse', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    async getItems () {
      try {
        this.loading = true

        const { data } = await PerformanceEvaluationsService.getItems({
          warehouse: this.query.warehouseId,
          startDate: this.$dayjs(`${this.query.year}-${this.query.month}-01`)
                      .startOf('month')
                      .hour(0)
                      .minute(0)
                      .second(0)
                      .utc()
                      .toISOString(),
          endDate: this.$dayjs(`${this.query.year}-${this.query.month}-01`)
                      .endOf('month')
                      .hour(23)
                      .minute(59)
                      .second(59)
                      .utc()
                      .toISOString()
        })

        this.items = this.weeks.map((week) => {
          const result = data.find((v) => v.week === week)
          let weekTitle = 'First week (Day 1 - 7)'

          if (week === 2) {
            weekTitle = 'Second week (Day 8 - 14)'
          } else if (week === 3) {
            weekTitle = 'Third week (Day 15 - 21)'
          } else if (week === 4) {
            weekTitle = 'Last week (Day 22 - End of month)'
          }

          if (result) {
            const topics = result.topics.map((topic) => {
              const found = this.mainTopics.find((v) => v.id === topic.topicType)

              return {
                ...topic,
                name: found?.name || topic.topicType,
                conditionType: found?.conditionType || topic.conditionType,
                formula: found?.formula || '-'
              }
            })

            return {
              ...result,
              weekTitle,
              topics
            }
          }

          return {
            id: null,
            week,
            weekTitle,
            month: this.query.month,
            year: this.query.year,
            warehouse: this.warehouse,
            topics: [],
            totalScore: 0
          }
        })
      } catch (error) {
        console.error('getItems', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.toolbar {
  position: sticky;
  top: 70px;
  left: 0;
  width: 100%;
  z-index: 5;
  background-color: #fff;
  padding: 1.25rem 1.875rem;
  border-radius: 0.625rem;
}
.toolbar span {
  font-weight: bold;
  font-size: 1.25rem;
}
.card-container {
  padding: 1.25rem 1.875rem;
  background-color: #fff;
  border-radius: 0.625rem;
  margin-top: 1.25rem;
}
.card-title {
  font-weight: bold;
  font-size: 1.35rem;
}
</style>
